import React, { useState, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import { useTranslation } from 'react-i18next';
import { useFilters } from '../../context/FilterContext';
import { useDashboardData } from '../../hooks/useDashboardData';
import { useQuery } from '@tanstack/react-query';
import './TopBottomItemsChart.css';
import '../charts/ChartStyles.css';

interface MenuItem {
  menu_group: string;
  menu_category: string;
  menu_item: string;
  net_sales: number | string; 
  quantity: string | number;
  percentage: number | string; 
}


const fetchTopMenuItems = async (
  salesforceId: string, 
  startDate: string, 
  endDate: string,
  menuGroup?: string,
  limit: number = 20
): Promise<MenuItem[]> => {
  const params = new URLSearchParams({
    startDate,
    endDate,
    limit: limit.toString()
  });
  
  if (menuGroup && menuGroup !== 'ALL') {
    params.append('menuGroup', menuGroup);
  }
  
  const response = await fetch(`/api/v1/dashboard/salesforce/${salesforceId}/top-menu-items?${params}`);
  if (!response.ok) {
    throw new Error('Failed to fetch top menu items');
  }
  return response.json();
};

const fetchBottomMenuItems = async (
  salesforceId: string, 
  startDate: string, 
  endDate: string,
  menuGroup?: string,
  limit: number = 20
): Promise<MenuItem[]> => {
  const params = new URLSearchParams({
    startDate,
    endDate,
    limit: limit.toString()
  });
  
  if (menuGroup && menuGroup !== 'ALL') {
    params.append('menuGroup', menuGroup);
  }
  
  const response = await fetch(`/api/v1/dashboard/salesforce/${salesforceId}/bottom-menu-items?${params}`);
  if (!response.ok) {
    throw new Error('Failed to fetch bottom menu items');
  }
  return response.json();
};

const TopBottomItemsChart: React.FC = () => {
  const { t } = useTranslation();
  const { data: dashboardData, loading, error, salesforceId } = useDashboardData();
  const { isFiltersApplying, dateRange } = useFilters();

  const [showAll, setShowAll] = useState<boolean>(false);
  const [selectedMenuGroup, setSelectedMenuGroup] = useState<string>('ALL');
  const [chartInstance, setChartInstance] = useState<any>(null);

  
  const allTopMenuItems = (dashboardData?.chartData as any)?.topMenuItems || [];
  const allBottomMenuItems = (dashboardData?.chartData as any)?.bottomMenuItems || [];

  
  const availableMenuGroups = ['ALL', ...Array.from(new Set([
    ...allTopMenuItems.map((item: MenuItem) => item.menu_group),
    ...allBottomMenuItems.map((item: MenuItem) => item.menu_group)
  ]))];

  
  const { data: filteredTopMenuItems, isLoading: topItemsLoading } = useQuery({
    queryKey: ['topMenuItems', 'v2', salesforceId, dateRange.startDate, dateRange.endDate, selectedMenuGroup, 20],  
    queryFn: () => fetchTopMenuItems(salesforceId!, dateRange.startDate, dateRange.endDate, selectedMenuGroup, 20),
    enabled: selectedMenuGroup !== 'ALL' && !!salesforceId && !!dateRange.startDate && !!dateRange.endDate,
    staleTime: 12 * 60 * 60 * 1000, 
  });

  
  const { data: filteredBottomMenuItems, isLoading: bottomItemsLoading } = useQuery({
    queryKey: ['bottomMenuItems', 'v2', salesforceId, dateRange.startDate, dateRange.endDate, selectedMenuGroup, 20],  
    queryFn: () => fetchBottomMenuItems(salesforceId!, dateRange.startDate, dateRange.endDate, selectedMenuGroup, 20),
    enabled: selectedMenuGroup !== 'ALL' && !!salesforceId && !!dateRange.startDate && !!dateRange.endDate,
    staleTime: 12 * 60 * 60 * 1000, 
  });

  
  const topMenuItems = selectedMenuGroup === 'ALL' 
    ? allTopMenuItems 
    : (filteredTopMenuItems || []);
  
  const bottomMenuItems = selectedMenuGroup === 'ALL' 
    ? allBottomMenuItems 
    : (filteredBottomMenuItems || []);

  
  const isGroupFilterLoading = selectedMenuGroup !== 'ALL' && (topItemsLoading || bottomItemsLoading);

  
  const canShowAllItems = topMenuItems.length > 10 && bottomMenuItems.length > 10;

  
  useEffect(() => {
    setShowAll(false);
  }, [selectedMenuGroup]);

  
  const isLaptop = window.innerWidth <= 1600;
  const isIPad = window.innerWidth <= 1024;
  const isIPhone = window.innerWidth <= 480;

  const colorPalette = {
    topColor: '#F97F76',
    bottomColor: '#F97F76',
    textColor: '#333333',
    dividerColor: '#474646',
    hoverColor: 'rgba(249, 127, 118, 0.15)'
  };

  
  const getFilteredMenuData = () => {
    return {
      topItems: topMenuItems,
      bottomItems: bottomMenuItems
    };
  };

  useEffect(() => {
    if (!showAll || !chartInstance) return;

    const chartDom = chartInstance.getDom();
    if (!chartDom) return;
    
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      
      const option = chartInstance.getOption();
      const dataZoom = option.dataZoom?.[0];
      if (!dataZoom) return;
      
      const currentStart = dataZoom.start || 0;
      const currentEnd = dataZoom.end || 50;
      const range = currentEnd - currentStart;
      
      const delta = e.deltaY > 0 ? -8 : 8;
      let newStart = currentStart + delta;
      let newEnd = currentEnd + delta;
      
      if (newStart < 0) {
        newStart = 0;
        newEnd = range;
      }
      if (newEnd > 100) {
        newEnd = 100;
        newStart = 100 - range;
      }
      
      chartInstance.dispatchAction({
        type: 'dataZoom',
        dataZoomIndex: 0,
        start: newStart,
        end: newEnd
      });
    };
    
    chartDom.addEventListener('wheel', handleWheel, { passive: false });
    
    return () => {
      if (chartDom) {
        chartDom.removeEventListener('wheel', handleWheel);
      }
    };
  }, [showAll, chartInstance]);

  useEffect(() => {
    setChartInstance(null);
  }, [showAll]);

  const formatItemName = (item: MenuItem): string => {
    return `${item.menu_category}\n${item.menu_item}`;
  };

  const getOption = () => {
    const { topItems, bottomItems } = getFilteredMenuData();
    
    if (topItems.length === 0 || bottomItems.length === 0) {
      return {};
    }

    
    const maxAvailableItems = Math.min(topItems.length, bottomItems.length);
    const requestedDisplayCount = showAll ? 20 : 10;
    const actualDisplayCount = Math.min(requestedDisplayCount, maxAvailableItems);

    const topDisplayItems = topItems.slice(0, actualDisplayCount).reverse();
    const bottomDisplayItems = bottomItems.slice(0, actualDisplayCount).reverse();

    const topItemNames = topDisplayItems.map((item: MenuItem) => formatItemName(item));
    const topItemSales = topDisplayItems.map((item: MenuItem) => {
      const sales = typeof item.net_sales === 'string' 
        ? parseFloat(item.net_sales.replace(/[$,]/g, '')) 
        : item.net_sales;
      return sales || 0;
    });
    const topItemPercentages = topDisplayItems.map((item: MenuItem) => item.percentage || 0);
    
    const bottomItemNames = bottomDisplayItems.map((item: MenuItem) => formatItemName(item));
    const bottomItemSales = bottomDisplayItems.map((item: MenuItem) => {
      const sales = typeof item.net_sales === 'string' 
        ? parseFloat(item.net_sales.replace(/[$,]/g, '')) 
        : item.net_sales;
      return sales || 0;
    });
    const bottomItemPercentages = bottomDisplayItems.map((item: MenuItem) => item.percentage || 0);

    const rankNumbers = Array.from({ length: actualDisplayCount }, (_, i) => actualDisplayCount - i);

    const maxTopSales = topItemSales.length > 0 ? Math.max(...topItemSales) : 0;
    const maxBottomSales = bottomItemSales.length > 0 ? Math.max(...bottomItemSales) : 0;
    const maxSales = Math.max(maxTopSales, maxBottomSales, 1); 
    
    
    const dividerPosition = isIPhone ? '55%' : (isIPad ? '54%' : (isLaptop ? '53%' : '52%'));

    const chartOption: any = {
      title: [
        {
          text: t('topBottomChart.topItems', { count: actualDisplayCount }),
          left: isIPhone ? '27%' : (isIPad ? '28%' : (isLaptop ? '29%' : '30%')),
          top: 0,
          textAlign: 'center',
          textStyle: {
            fontSize: isIPhone ? 14 : (isIPad ? 15 : (isLaptop ? 16 : 18)),
            fontWeight: 'bold',
            color: colorPalette.textColor
          }
        },
        {
          text: t('topBottomChart.bottomItems', { count: actualDisplayCount }),
          left: isIPhone ? '77%' : (isIPad ? '77%' : (isLaptop ? '76%' : '76%')),
          top: 0,
          textAlign: 'center',
          textStyle: {
            fontSize: isIPhone ? 14 : (isIPad ? 15 : (isLaptop ? 16 : 18)),
            fontWeight: 'bold',
            color: colorPalette.textColor
          }
        }
      ],
      graphic: [
        {
          type: 'line',
          shape: {
            x1: dividerPosition,
            y1: '60px',
            x2: dividerPosition,
            y2: '97%'
          },
          style: {
            stroke: colorPalette.dividerColor,
            lineWidth: 3,
            lineDash: [8, 4]
          },
          silent: true,
          z: 1
        }
      ],
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        formatter: function(params: any) {
          if (params[0].seriesIndex === 0) {
            return '';
          }
          
          const dataIndex = params[0].dataIndex;
          const isTop = params[0].seriesIndex === 1;
          const item = isTop ? topDisplayItems[dataIndex] : bottomDisplayItems[dataIndex];
          
          return `<div style="padding: ${isIPhone ? '2px' : '3px'}; border-left: 4px solid ${colorPalette.topColor}; font-size: ${isIPhone ? '10px' : 'inherit'};">
            <div style="font-weight: bold; margin-bottom: ${isIPhone ? '2px' : (isIPad ? '5px' : (isLaptop ? '6px' : '8px'))}; font-size: ${isIPhone ? '9px' : (isIPad ? '12px' : (isLaptop ? '13px' : '14px'))};">${item.menu_item}</div>
            <div style="display: flex; justify-content: space-between; margin-bottom: ${isIPhone ? '1px' : (isIPad ? '4px' : (isLaptop ? '4px' : '5px'))};">
              <span style="margin-right: ${isIPhone ? '8px' : '15px'};">${t('topBottomChart.category')}:</span>
              <span style="font-weight: bold;">${item.menu_category} (${item.menu_group})</span>
            </div>
            <div style="display: flex; justify-content: space-between; margin-bottom: ${isIPhone ? '1px' : (isIPad ? '4px' : (isLaptop ? '4px' : '5px'))};">
              <span style="margin-right: ${isIPhone ? '8px' : '15px'};">${t('topBottomChart.sales')}:</span>
              <span style="font-weight: bold;">${typeof item.net_sales === 'string' ? item.net_sales : '$' + (item.net_sales || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
            <div style="display: flex; justify-content: space-between; margin-bottom: ${isIPhone ? '1px' : (isIPad ? '4px' : (isLaptop ? '4px' : '5px'))};">
              <span style="margin-right: ${isIPhone ? '8px' : '15px'};">${t('topBottomChart.quantity')}:</span>
              <span style="font-weight: bold;">${item.quantity || 0}</span>
            </div>
            <div style="display: flex; justify-content: space-between;">
              <span style="margin-right: ${isIPhone ? '8px' : '15px'};">${t('topBottomChart.percentage')}:</span>
              <span style="font-weight: bold;">${(item.percentage || 0).toFixed(2)}%</span>
            </div>
          </div>`;
        }
      },
      grid: [
        {
          left: '3%',
          right: isIPad ? '93%' : (isLaptop ? '92%' : '91%'),
          top: '60px',
          bottom: '3%',
          containLabel: true
        },
        {
          left: isIPhone ? '9%' : (isIPad ? '7%' : (isLaptop ? '8%' : '9%')),
          right: isIPhone ? '52%' : (isIPad ? '62%' : (isLaptop ? '61%' : '60%')),
          top: '60px',
          bottom: '3%',
          containLabel: true
        },
        {
          left: isIPhone ? '60%' : (isIPad ? '60%' : (isLaptop ? '59%' : '58%')),
          right: isIPhone ? '4%' : (isIPad ? '9%' : (isLaptop ? '10%' : '12%')),
          top: '60px',
          bottom: '3%',
          containLabel: true
        }
      ],
      xAxis: [
        {
          type: 'value',
          gridIndex: 0,
          max: 1,
          show: false
        },
        {
          type: 'value',
          gridIndex: 1,
          max: maxSales * 1.2,
          show: false
        },
        {
          type: 'value',
          gridIndex: 2,
          max: maxSales * 1.2,
          show: false
        }
      ],
      yAxis: [
        {
          type: 'category',
          gridIndex: 0,
          data: rankNumbers,
          axisTick: { show: false },
          axisLine: { show: false },
          splitLine: { show: false },
          axisLabel: {
            fontWeight: 'bold',
            fontSize: isIPhone ? 10 : (isIPad ? 11 : (isLaptop ? 12 : 14)),
            color: colorPalette.textColor
          }
        },
        {
          type: 'category',
          gridIndex: 1,
          data: topItemNames,
          axisTick: { show: false },
          axisLine: { show: false },
          splitLine: { show: false },
          axisLabel: {
            formatter: function(value: string) {
              return value;
            },
            fontWeight: 'bold',
            fontSize: isIPhone ? 8 : (isIPad ? 9 : (isLaptop ? 10 : 11)),
            color: colorPalette.textColor,
            width: 150,
            overflow: 'break',
            lineHeight: isIPhone ? 10 : (isIPad ? 11 : (isLaptop ? 12 : 13))
          }
        },
        {
          type: 'category',
          gridIndex: 2,
          data: bottomItemNames,
          axisTick: { show: false },
          axisLine: { show: false },
          splitLine: { show: false },
          axisLabel: {
            formatter: function(value: string) {
              return value;
            },
            fontWeight: 'bold',
            fontSize: isIPhone ? 8 : (isIPad ? 9 : (isLaptop ? 10 : 11)),
            color: colorPalette.textColor,
            width: 150,
            overflow: 'break',
            lineHeight: isIPhone ? 10 : (isIPad ? 11 : (isLaptop ? 12 : 13))
          }
        }
      ],
      series: [
        {
          name: '순위',
          type: 'bar',
          xAxisIndex: 0,
          yAxisIndex: 0,
          data: rankNumbers.map((rank, index) => ({
            value: 1,
            itemStyle: {
              color: 'transparent'
            }
          })),
          barWidth: '100%',
          label: {
            show: false
          },
          silent: true
        },
        {
          name: t('topBottomChart.topRank'),
          type: 'bar',
          xAxisIndex: 1,
          yAxisIndex: 1,
          data: topItemSales.map((value: number, index: number) => ({
            value,
            itemStyle: {
              color: colorPalette.topColor,
              borderRadius: [0, 4, 4, 0]
            },
            emphasis: {
              itemStyle: {
                color: '#E66A60',
                shadowBlur: isLaptop ? 15 : 20,
                shadowColor: 'rgba(249, 127, 118, 0.6)',
                borderColor: '#E66A60',
                borderWidth: 3
              }
            },
            label: {
              show: !isIPhone,
              position: 'right',
              formatter: function(params: any) {
                const percentage = topItemPercentages[params.dataIndex];
                const formattedValue = '$' + params.value.toLocaleString('en-US', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                });
                const fullLabel = `${formattedValue} (${(percentage || 0).toFixed(1)}%)`;
                
                
                const maxLength = isIPad ? 15 : (isLaptop ? 18 : 20);
                
                
                if (fullLabel.length > maxLength) {
                  return `${formattedValue}\n(${(percentage || 0).toFixed(1)}%)`;
                }
                return fullLabel;
              },
              fontSize: isLaptop ? 9 : 10,
              fontWeight: 'bold',
              color: colorPalette.textColor
            }
          })),
          barWidth: isLaptop ? '65%' : '70%'
        },
        {
          name: t('topBottomChart.bottomRank'),
          type: 'bar',
          xAxisIndex: 2,
          yAxisIndex: 2,
          data: bottomItemSales.map((value: number, index: number) => ({
            value,
            itemStyle: {
              color: colorPalette.bottomColor,
              borderRadius: [0, 4, 4, 0]
            },
            emphasis: {
              itemStyle: {
                color: '#E66A60',
                shadowBlur: isLaptop ? 15 : 20,
                shadowColor: 'rgba(249, 127, 118, 0.6)',
                borderColor: '#E66A60',
                borderWidth: 3
              }
            },
            label: {
              show: !isIPhone,
              position: 'right',
              formatter: function(params: any) {
                const percentage = bottomItemPercentages[params.dataIndex];
                const formattedValue = '$' + params.value.toLocaleString('en-US', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                });
                const fullLabel = `${formattedValue} (${(percentage || 0).toFixed(1)}%)`;
                
                
                const maxLength = isIPad ? 15 : (isLaptop ? 18 : 20);
                
                
                if (fullLabel.length > maxLength) {
                  return `${formattedValue}\n(${(percentage || 0).toFixed(1)}%)`;
                }
                return fullLabel;
              },
              fontSize: isLaptop ? 9 : 10,
              fontWeight: 'bold',
              color: colorPalette.textColor
            }
          })),
          barWidth: isLaptop ? '65%' : '70%'
        }
      ]
    };

    if (showAll) {
      chartOption.dataZoom = [
        {
          type: 'slider',
          yAxisIndex: [0, 1, 2],
          start: 50,
          end: 100,
          width: isIPhone ? 14 : (isIPad ? 15 : (isLaptop ? 16 : 18)),
          right: 2,
          top: '60px',
          bottom: '3%',
          handleSize: '100%',
          showDetail: false,
          brushSelect: false,
          zoomLock: true,
          moveOnMouseMove: false,
          moveOnMouseWheel: false,
          preventDefaultMouseMove: true,
          backgroundColor: 'rgba(240, 240, 240, 0.8)',
          fillerColor: colorPalette.topColor,
          borderColor: colorPalette.dividerColor,
          handleStyle: {
            color: colorPalette.topColor,
            borderColor: '#fff',
            borderWidth: 2,
            shadowBlur: 3,
            shadowColor: 'rgba(0, 0, 0, 0.3)'
          },
          dataBackground: {
            lineStyle: {
              color: 'rgba(200, 200, 200, 0.5)'
            },
            areaStyle: {
              color: 'rgba(230, 230, 230, 0.3)'
            }
          },
          selectedDataBackground: {
            lineStyle: {
              color: colorPalette.topColor
            },
            areaStyle: {
              color: 'rgba(249, 127, 118, 0.2)'
            }
          }
        }
      ];
    }

    return chartOption;
  };

  const onChartReady = (chartInstance: any) => {
    setChartInstance(chartInstance);
    
    chartInstance.on('mouseover', { seriesIndex: 1 }, (params: any) => {
      const dataIndex = params.dataIndex;
      
      const option = chartInstance.getOption();
      const chartHeight = chartInstance.getHeight();
      const chartWidth = chartInstance.getWidth();
      const topOffset = 60;
      const bottomOffset = chartHeight * 0.03;
      const availableHeight = chartHeight - topOffset - bottomOffset;
      
      let visibleCount = 10; 
      
      const itemHeight = availableHeight / visibleCount;
      const yPosition = topOffset + (dataIndex * itemHeight);
      
      const filteredGraphic = (option.graphic || []).filter((item: any) => item.id !== 'row-hover-bg');
      
      chartInstance.setOption({
        graphic: [
          ...filteredGraphic,
          {
            id: 'row-hover-bg',
            type: 'rect',
            shape: {
              x: 0,
              y: yPosition - 2,
              width: chartWidth,
              height: itemHeight + 4
            },
            style: {
              fill: 'rgba(249, 127, 118, 0.2)',
              stroke: 'rgba(249, 127, 118, 0.4)',
              strokeWidth: 1
            },
            silent: true,
            z: 0
          }
        ]
      });
      
      chartInstance.dispatchAction({
        type: 'highlight',
        seriesIndex: [0, 1, 2],
        dataIndex: dataIndex
      });
    });

    chartInstance.on('mouseout', { seriesIndex: 1 }, (params: any) => {
      
      const option = chartInstance.getOption();
      const filteredGraphic = (option.graphic || []).filter((item: any) => item.id !== 'row-hover-bg');
      
      chartInstance.setOption({
        graphic: filteredGraphic
      });
      
      chartInstance.dispatchAction({
        type: 'downplay',
        seriesIndex: [0, 1, 2]
      });
    });

    chartInstance.on('mouseover', { seriesIndex: 2 }, (params: any) => {
      const dataIndex = params.dataIndex;
      
      const option = chartInstance.getOption();
      const chartHeight = chartInstance.getHeight();
      const chartWidth = chartInstance.getWidth();
      const topOffset = 60;
      const bottomOffset = chartHeight * 0.03;
      const availableHeight = chartHeight - topOffset - bottomOffset;
      
      let visibleCount = 10; 
      
      const itemHeight = availableHeight / visibleCount;
      const yPosition = topOffset + (dataIndex * itemHeight);
      
      const filteredGraphic = (option.graphic || []).filter((item: any) => item.id !== 'row-hover-bg');
      
      chartInstance.setOption({
        graphic: [
          ...filteredGraphic,
          {
            id: 'row-hover-bg',
            type: 'rect',
            shape: {
              x: 0,
              y: yPosition - 2,
              width: chartWidth,
              height: itemHeight + 4
            },
            style: {
              fill: 'rgba(249, 127, 118, 0.2)',
              stroke: 'rgba(249, 127, 118, 0.4)',
              strokeWidth: 1
            },
            silent: true,
            z: 0
          }
        ]
      });
      
      chartInstance.dispatchAction({
        type: 'highlight',
        seriesIndex: [0, 1, 2],
        dataIndex: dataIndex
      });
    });

    chartInstance.on('mouseout', { seriesIndex: 2 }, (params: any) => {
      
      const option = chartInstance.getOption();
      const filteredGraphic = (option.graphic || []).filter((item: any) => item.id !== 'row-hover-bg');
      
      chartInstance.setOption({
        graphic: filteredGraphic
      });
      
      chartInstance.dispatchAction({
        type: 'downplay',
        seriesIndex: [0, 1, 2]
      });
    });

    if (showAll) {
      setTimeout(() => {
        if (chartInstance && !chartInstance.isDisposed()) {
          chartInstance.dispatchAction({
            type: 'dataZoom',
            dataZoomIndex: 0,
            start: 50,
            end: 100
          });
        }
      }, 100);
    }
  };

  if (loading || isFiltersApplying || isGroupFilterLoading) {
    return (
      <div className="top-bottom-chart">
        <div className="top-bottom-title-band">
          <h2 style={{
            fontSize: isLaptop ? '16px' : '18px',
            padding: isLaptop ? '8px 20px' : '10px 25px'
          }}>{t('topBottomChart.title')}</h2>
        </div>
        <div className="top-bottom-chart-container chart-container">
          <div className="top-bottom-loading">
            <div className="top-bottom-loading-spinner"></div>
            <div className="top-bottom-loading-text" style={{ fontSize: isLaptop ? '14px' : '16px' }}>
              {isFiltersApplying ? t('topBottomChart.filterApplying') : 
               isGroupFilterLoading ? t('topBottomChart.groupLoading', 'Loading group data...') : 
               t('topBottomChart.loading')}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="top-bottom-chart">
        <div className="top-bottom-title-band">
          <h2 style={{
            fontSize: isLaptop ? '16px' : '18px',
            padding: isLaptop ? '8px 20px' : '10px 25px'
          }}>{t('topBottomChart.title')}</h2>
        </div>
        <div className="top-bottom-chart-container">
          <div className="top-bottom-error">
            <div className="top-bottom-error-icon">⚠️</div>
            <div className="top-bottom-error-text" style={{ fontSize: isIPhone ? '12px' : (isIPad ? '13px' : (isLaptop ? '14px' : '16px')) }}>{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="top-bottom-chart">
      {isFiltersApplying && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <span style={{ fontSize: isIPhone ? '12px' : (isIPad ? '13px' : (isLaptop ? '14px' : '16px')) }}>{t('topBottomChart.filterApplying')}</span>
        </div>
      )}
      
      <div className="top-bottom-title-band">
        <h2 style={{
          fontSize: isIPhone ? '14px' : (isIPad ? '15px' : (isLaptop ? '16px' : '18px')),
          padding: isIPhone ? '6px 15px' : (isIPad ? '7px 18px' : (isLaptop ? '8px 20px' : '10px 25px'))
        }}>{t('topBottomChart.title')}</h2>
      </div>
      <div className="top-bottom-chart-container">
        <div className="top-bottom-controls" style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: isIPhone ? '8px' : (isIPad ? '10px' : (isLaptop ? '12px' : '15px'))
        }}>
          <div className="menu-group-filter" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <select
              value={selectedMenuGroup}
              onChange={(e) => setSelectedMenuGroup(e.target.value)}
              disabled={isFiltersApplying || isGroupFilterLoading}
              style={{
                fontSize: isIPhone ? '10px' : (isIPad ? '11px' : (isLaptop ? '11px' : '13px')),
                padding: isIPhone ? '3px 8px' : (isIPad ? '4px 10px' : (isLaptop ? '4px 12px' : '5px 15px')),
                border: '1px solid #474646',
                borderRadius: '4px',
                backgroundColor: '#474646',
                color: '#fff',
                fontWeight: 'bold',
                cursor: (isFiltersApplying || isGroupFilterLoading) ? 'not-allowed' : 'pointer',
                outline: 'none',
                opacity: (isFiltersApplying || isGroupFilterLoading) ? 0.6 : 1
              }}
            >
              {availableMenuGroups.map(group => (
                <option key={group} value={group} style={{ backgroundColor: '#474646', color: '#fff' }}>
                  {group === 'ALL' ? t('topBottomChart.allGroups', 'All Groups') : group}
                </option>
              ))}
            </select>
            
            {selectedMenuGroup !== 'ALL' && (
              <button
                onClick={() => setSelectedMenuGroup('ALL')}
                disabled={isFiltersApplying || isGroupFilterLoading}
                style={{
                  width: isIPhone ? '20px' : (isIPad ? '22px' : (isLaptop ? '24px' : '26px')),
                  height: isIPhone ? '20px' : (isIPad ? '22px' : (isLaptop ? '24px' : '26px')),
                  backgroundColor: '#474646',
                  border: '1px solid #474646',
                  borderRadius: '4px',
                  color: '#fff',
                  cursor: (isFiltersApplying || isGroupFilterLoading) ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: isIPhone ? '12px' : (isIPad ? '13px' : (isLaptop ? '14px' : '16px')),
                  opacity: (isFiltersApplying || isGroupFilterLoading) ? 0.6 : 1,
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (!isFiltersApplying && !isGroupFilterLoading) {
                    e.currentTarget.style.backgroundColor = '#555';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isFiltersApplying && !isGroupFilterLoading) {
                    e.currentTarget.style.backgroundColor = '#474646';
                  }
                }}
                title="Reset to All Groups"
              >
                ↻
              </button>
            )}
          </div>
          
          {canShowAllItems && (
            <button 
              className={`top-bottom-button ${showAll ? 'top-bottom-button-active' : ''}`} 
              onClick={() => setShowAll(!showAll)}
              disabled={isFiltersApplying || isGroupFilterLoading}
              style={{
                fontSize: isIPhone ? '10px' : (isIPad ? '11px' : (isLaptop ? '11px' : '13px')),
                padding: isIPhone ? '3px 8px' : (isIPad ? '4px 10px' : (isLaptop ? '4px 12px' : '5px 15px'))
              }}
            >
              {showAll ? t('topBottomChart.showTop10') : t('topBottomChart.showTop20')}
            </button>
          )}
        </div>
        <ReactECharts 
          key={`chart-${showAll ? '20' : '10'}-${selectedMenuGroup}`}
          option={getOption()} 
          style={{ height: isIPhone ? '100%' : (isIPad ? '360px' : (isLaptop ? '380px' : '460px')), width: '100%' }}
          className="top-bottom-echarts"
          opts={{ renderer: 'canvas' }}
          onChartReady={onChartReady}
        />
      </div>
    </div>
  );
};

export default TopBottomItemsChart;